import { useAuth } from "@assetwatch/auth";
import { useQuery } from "@tanstack/react-query";
import { useFeatureFlags } from "../../components/LaunchDarkly/useFeatureFlags";
import { fetchMpGraphTypes, fetchMpVibrationGraphs } from "./api";

/**
 * react-query hooks for the MP Graphs page's ML API endpoint.
 *
 * Both take the same two things from context — the signed-in user and the
 * feature flags — and hand them to the fetcher, which is where the access check
 * and the request live.
 */

/** The catalog reads no data and changes only when the backend ships a new
 * source, so navigation shouldn't re-request it; a page reload picks up
 * additions. */
const CATALOG_STALE_TIME = 30 * 60_000;
const DATA_STALE_TIME = 60_000;

/** The graph catalog behind the chart picker. */
export function useMpGraphTypes() {
  const { user } = useAuth();
  const flags = useFeatureFlags();

  return useQuery({
    queryKey: ["mpGraphTypes"],
    queryFn: () => fetchMpGraphTypes({ user, flags }),
    staleTime: CATALOG_STALE_TIME,
    meta: { errorMessage: "Could not load the graph catalog." },
  });
}

export function useMpVibrationGraphs({
  monitoringPointId,
  timeRangeDays,
  graphs,
}: {
  monitoringPointId: string | undefined;
  timeRangeDays: number;
  graphs: string[];
}) {
  const { user } = useAuth();
  const flags = useFeatureFlags();

  // Sorted so that reordering the same selection reuses the cached response —
  // the backend returns graphs in catalog order regardless of request order.
  const graphKeys = [...graphs].sort();

  return useQuery({
    queryKey: [
      "mpVibrationGraphs",
      monitoringPointId,
      timeRangeDays,
      graphKeys,
    ],
    queryFn: () =>
      fetchMpVibrationGraphs({
        user,
        flags,
        monitoringPointId: monitoringPointId ?? "",
        timeRangeDays,
        graphs: graphKeys,
      }),
    enabled: Boolean(monitoringPointId) && graphKeys.length > 0,
    staleTime: DATA_STALE_TIME,
    meta: { errorMessage: "Could not load monitoring point graphs." },
  });
}
